import { Modal } from "antd";
import React from "react";
import { useBlocker } from "react-router-dom";

interface IConfirmLeaveOptions {
  /** Whether leaving should be confirmed at all. */
  when: boolean;
  title: string;
  content: string;
}

/**
 * Asks the user to confirm before leaving the current page — in-app
 * navigation and the browser back button get a confirm modal, and a reload or
 * tab close gets the browser's own prompt.
 *
 * Call the returned `allowLeave` right before a navigation that should not be
 * confirmed (e.g. after a successful save).
 */
export const useConfirmLeave = ({
  when,
  title,
  content
}: IConfirmLeaveOptions) => {
  const allowLeaveRef = React.useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when &&
      !allowLeaveRef.current &&
      currentLocation.pathname !== nextLocation.pathname
  );

  React.useEffect(() => {
    if (blocker.state !== "blocked") return;
    Modal.confirm({
      title,
      content,
      okText: "Leave",
      okButtonProps: { danger: true },
      cancelText: "Stay",
      centered: true,
      onOk: blocker.proceed,
      onCancel: blocker.reset
    });
  }, [blocker, title, content]);

  React.useEffect(() => {
    if (!when) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [when]);

  return React.useCallback(() => {
    allowLeaveRef.current = true;
  }, []);
};
